import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Zap, Rocket, ArrowRight, Loader2, MapPin } from "lucide-react";

/**
 * ReadyToBlastWidget — top-of-Dashboard tile that surfaces every hot-stage
 * deal (Under Contract · Marketing · Pending Close) along with its cached
 * buyer-match count. One tap on any row jumps straight into that
 * property's Match & Blast tab where the operator can send the campaign.
 *
 * Renders NOTHING when the operator has no hot-stage deals — no wall of
 * zeros on empty workspaces. Backed by GET /v3/disposition/ready-to-blast.
 */
export default function ReadyToBlastWidget() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/v3/disposition/ready-to-blast?limit=6")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data || (data.totals?.deals ?? 0) === 0) return null;

  const { totals, items } = data;
  const totalMatched = totals.matched_buyers || 0;

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 via-white to-amber-50 shadow-sm"
      data-testid="ready-to-blast-widget"
    >
      {/* Grain overlay for texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(ellipse at 80% 0%, #F59E0B22 0%, transparent 55%)" }}
      />
      <div className="relative p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center shadow-md">
              <Zap size={22} />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.14em] text-amber-800">
                Match &amp; Blast
              </div>
              <h3 className="font-display font-black text-xl md:text-2xl text-slate-900 tracking-tight leading-none mt-0.5">
                Ready to Blast
              </h3>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">
              Pending
            </div>
            <div className="text-2xl font-display font-black text-slate-900 tabular-nums leading-none">
              {totals.deals}
            </div>
            <div className="text-[10px] text-slate-500">
              {totalMatched} buyer{totalMatched === 1 ? "" : "s"} across all
            </div>
          </div>
        </div>

        <div className="space-y-2">
          {items.map((it) => (
            <BlastRow key={it.property_id} item={it} onOpen={() => nav(`/v3/properties/${it.property_id}?tab=buyers`)} />
          ))}
        </div>

        <div className="mt-4 pt-3 border-t border-amber-200/70 flex items-center justify-between text-[11px]">
          <div className="text-slate-600 leading-tight">
            Every deal you sign should hit every matching buyer within the hour.
          </div>
          <Link
            to="/v3/crm/pipeline"
            className="inline-flex items-center gap-1 font-bold text-amber-800 hover:text-amber-900"
            data-testid="ready-to-blast-view-all"
          >
            Full pipeline <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function BlastRow({ item, onOpen }) {
  const addr = item.address || {};
  const street = addr.street || "Unaddressed property";
  const cityLine = [addr.city, addr.state].filter(Boolean).join(", ");
  const stageLabels = {
    under_contract: "Under Contract",
    marketing: "Marketing",
    pending_close: "Pending Close",
  };
  const count = item.match_count || 0;
  const hasMatches = count > 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-3 rounded-lg bg-white/80 hover:bg-white border border-slate-200 hover:border-amber-400 hover:shadow-sm px-3 py-2.5 text-left transition-all group"
      data-testid={`ready-to-blast-row-${item.property_id}`}
    >
      {item.hero_photo_url ? (
        <img
          src={item.hero_photo_url}
          alt=""
          className="w-11 h-11 rounded-lg object-cover shrink-0"
        />
      ) : (
        <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <MapPin size={16} className="text-slate-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-bold text-slate-900 truncate">{street}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {cityLine} · <span className="text-slate-700 font-semibold">{stageLabels[item.stage] || item.stage}</span>
          {item.top_buyer_name && hasMatches && (
            <> · top: <span className="text-slate-800 font-semibold">{item.top_buyer_name}</span></>
          )}
        </div>
      </div>
      {hasMatches ? (
        <span className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-br from-amber-600 to-amber-800 group-hover:from-amber-700 group-hover:to-amber-900 text-white px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.06em] shadow-sm shrink-0">
          <Rocket size={11} /> Blast {count}
        </span>
      ) : (
        <span className="inline-flex items-center rounded-lg bg-slate-100 text-slate-600 px-2.5 py-1 text-[11px] font-bold shrink-0">
          Run Match
        </span>
      )}
    </button>
  );
}
